import { Component, OnInit, TemplateRef } from "@angular/core";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import {
  FormBuilder,
  FormGroup,
  FormArray,
  Validators,
  AbstractControl,
} from "@angular/forms";
import { DataService } from "src/app/shared/Services/data.service";

const STATUS_MAP: Record<string, string> = {
  todo: "Created",
  planning: "Not Yet Started",
  production: "In Progress",
  onhold: "Pause",
  completed: "Completed",
};

const PROCESS_STATUS_LIST = [
  "Created",
  "Not Yet Started",
  "In Progress",
  "Pause",
  "Completed",
];

const DRAG_RULES: Record<string, string[]> = {
  todo: ["planning"],
  planning: [],
  production: [],
  onhold: [],
  completed: [],
};

const ABORTABLE_COLS = ["planning", "production", "onhold"];

@Component({
  selector: "app-workorder",
  templateUrl: "./workorder.component.html",
  styleUrls: ["./workorder.component.scss"],
})
export class WorkorderComponent implements OnInit {
  columns = [
    { id: "todo", title: "To Do", cls: "col-todo", icon: "ri-stack-fill" },
    { id: "planning", title: "Planning", cls: "col-planning", icon: "ri-folder-chart-fill" },
    { id: "production", title: "Production", cls: "col-production", icon: "mdi mdi-video-input-component" },
    { id: "onhold", title: "On Hold", cls: "col-onhold", icon: "ri-pause-circle-line" },
    { id: "completed", title: "Completed", cls: "col-completed", icon: "ri-checkbox-circle-line" },
  ];

  machineData: any[] = [];
  processStatusList = PROCESS_STATUS_LIST;
  jobMasterList: any[] = [];

  // ── Cards ──────────────────────────────────────────────────────────────────
  private _cards: any[] = [];
  columnMap = new Map<string, any[]>();

  set cards(val: any[]) { this._cards = val; this.rebuildColumnMap(); }
  get cards(): any[] { return this._cards; }

  private rebuildColumnMap(): void {
    const map = new Map<string, any[]>();
    for (const col of this.columns) map.set(col.id, []);
    for (const card of this._cards) {
      if (!map.has(card.col)) map.set(card.col, []);
      map.get(card.col)!.push(card);
    }
    this.columnMap = map;
  }

  // ── Row validation cache ───────────────────────────────────────────────────
  rowErrorCache = new Map<string, { errors: string[]; qtyExceeds: boolean }>();

  // ── Edit / Delete ──────────────────────────────────────────────────────────
  isEditMode = false;
  editingCard: any = null;
  existingBatchCount = 0;

  showDeleteModal = false;
  cardToDelete: any = null;

  // ── Abort ──────────────────────────────────────────────────────────────────
  showAbortModal = false;
  cardToAbort: any = null;
  abortReason = "";
  abortReasonError = false;

  // ── Drag / drop ────────────────────────────────────────────────────────────
  dragId: string | null = null;
  dragOverCol: string | null = null;
  pendingMove: any = null;
  showConfirmModal = false;

  // ── Form ───────────────────────────────────────────────────────────────────
  workOrderForm!: FormGroup;
  toastMsg = "";
  toastType: "success" | "error" = "success";
  toastVisible = false;
  private toastTimer: any;

  selectedJobProcesses: any[] = [];
  selectedJobId: string = "";
  submitted = false;

  user: any;
  custId: string = "";

  private readonly statusCssMap: Record<string, string> = {
    "Created": "not-created",
    "Not Yet Started": "not-started",
    "In Progress": "in-progress",
    "Pause": "on-hold",
    "Completed": "completed",
    "Aborted": "aborted",
  };
  private readonly badgeCssMap: Record<string, string> = {
    "Created": "badge-not-created",
    "Not Yet Started": "badge-not-started",
    "In Progress": "badge-in-progress",
    "Pause": "badge-on-hold",
    "Completed": "badge-completed",
    "Aborted": "badge-aborted",
  };
  readonly statusIconMap: Record<string, string> = {
    "Created": "ri-time-line",
    "Not Yet Started": "ri-time-line",
    "In Progress": "mdi mdi-progress-clock",
    "Pause": "ri-pause-circle-line",
    "Completed": "ri-checkbox-circle-line",
    "Aborted": "ri-forbid-line",
  };

  constructor(
    private modalService: NgbModal,
    private fb: FormBuilder,
    private dataservice: DataService
  ) {
    this.user = JSON.parse(sessionStorage.getItem("currentUser")!);
    this.custId = this.user["user"]["custId"];
    this.createForm();
    this.rebuildColumnMap();
  }

  ngOnInit(): void {
    this.getMachineData();
    this.getJobData();
    this.loadCards();
  }

  // ── API ────────────────────────────────────────────────────────────────────
  getJobData(): void {
    this.dataservice.get("jobOrder/masterList/" + this.custId).subscribe({
      next: (res: any) => { this.jobMasterList = res || []; },
      error: () => { this.jobMasterList = []; },
    });
  }

  loadCards(): void {
    this.dataservice.get(`workOrder/program?custId=${this.custId}`).subscribe({
      next: (res: any) => { this.cards = res || []; },
      error: () => { this.showToast("Failed to load work orders.", "error"); },
    });
  }

  getMachineData(): void {
    this.dataservice.get("api/device").subscribe({
      next: (res: any) => { this.machineData = res || []; },
    });
  }

  // ── Form builders ──────────────────────────────────────────────────────────
  createForm(): void {
    this.workOrderForm = this.fb.group({
      orderNumber: ["", Validators.required],
      jobName: ["", Validators.required],
      requiredQty: ["", [Validators.required, Validators.min(1)]],
      plannedStartTime: ["", Validators.required],
      plannedEndTime: ["", Validators.required],
      batches: this.fb.array([this.createBatch()]),
    });
    this.selectedJobProcesses = [];
    this.selectedJobId = "";
    this.rowErrorCache.clear();
    this.submitted = false;
    this.isEditMode = false;
    this.editingCard = null;
    this.existingBatchCount = 0;
  }

  createBatch(): FormGroup {
    return this.fb.group({
      batchNumber: [1],
      batchStatus: ["Not Yet Started"],
      batchStartTime: [{ value: "", disabled: true }],
      batchEndTime: [{ value: "", disabled: true }],
      machineRows: this.fb.array([this.createMachineRow()]),
    });
  }

  createMachineRow(): FormGroup {
    return this.fb.group({
      machine: ["", Validators.required],
      processId: ["p" + Date.now()],
      processName: ["", Validators.required],
      processStatus: ["Not Yet Started"],
      processstartTime: ["", Validators.required],
      processendTime: ["", Validators.required],
      plannedQty: ["", [Validators.required, Validators.min(1)]],
      capacity: [{ value: "", disabled: true }],
    });
  }

  get batches(): FormArray { return this.workOrderForm.get("batches") as FormArray; }

  getMachineRows(batchIndex: number): FormArray {
    return this.batches.at(batchIndex).get("machineRows") as FormArray;
  }

  // ── Control getters ────────────────────────────────────────────────────────
  get orderNumberCtrl(): AbstractControl { return this.workOrderForm.get("orderNumber")!; }
  get jobNameCtrl(): AbstractControl { return this.workOrderForm.get("jobName")!; }
  get requiredQtyCtrl(): AbstractControl { return this.workOrderForm.get("requiredQty")!; }
  get plannedStartCtrl(): AbstractControl { return this.workOrderForm.get("plannedStartTime")!; }
  get plannedEndCtrl(): AbstractControl { return this.workOrderForm.get("plannedEndTime")!; }

  isFieldInvalid(control: AbstractControl | null): boolean {
    if (!control) return false;
    return (this.submitted || control.touched) && control.invalid;
  }

  isWorkOrderTimesSet(): boolean {
    const start = this.workOrderForm.get("plannedStartTime")?.value;
    const end = this.workOrderForm.get("plannedEndTime")?.value;
    return !!(start && end && new Date(end) > new Date(start));
  }

  isPlannedTimeLockedByJob(): boolean {
    return this.isEditMode && (this.editingCard?.col === "todo" || this.editingCard?.col === "planning") && !this.jobNameCtrl.value;
  }

  private formatDateTimeLocal(date: Date): string {
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  }

  getMinWorkOrderStartTime(): string {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return this.formatDateTimeLocal(now);
  }

  getWorkOrderMin(): string { return this.workOrderForm.get("plannedStartTime")?.value ?? ""; }
  getWorkOrderMax(): string { return this.workOrderForm.get("plannedEndTime")?.value ?? ""; }

  // ── Batch lock logic ───────────────────────────────────────────────────────
  // isExistingBatch(batchIndex: number): boolean {
  //   if (!this.isEditMode) return false;
  //   if (this.editingCard?.col === "todo" || this.editingCard?.col === "planning") {
  //     return batchIndex < this.batches.length - 1;
  //   }
  //   return batchIndex < this.existingBatchCount;
  // }
  isExistingBatch(batchIndex: number): boolean {
    if (!this.isEditMode) return false;
    if (this.editingCard?.col === "planning") return false;
    if (this.editingCard?.col === "todo") {
      return batchIndex < this.batches.length - 1;
    }
    return batchIndex < this.existingBatchCount;
  }

  // ── Promote last batch when a batch/row is removed ─────────────────────────
  private promoteLastBatch(): void {
    if (!this.isEditMode || (this.editingCard?.col !== "todo" && this.editingCard?.col !== "planning")) return;

    const lastIdx = this.batches.length - 1;
    const batch = this.batches.at(lastIdx) as FormGroup;
    const rows = this.getMachineRows(lastIdx);

    // Enable the entire batch group first to clear group-level disabled flag
    batch.enable({ emitEvent: false });

    // Re-disable only auto-calc display fields
    batch.get("batchStartTime")?.disable({ emitEvent: false });
    batch.get("batchEndTime")?.disable({ emitEvent: false });

    // Enable all row fields except capacity
    rows.controls.forEach((rowCtrl: AbstractControl) => {
      const fg = rowCtrl as FormGroup;
      fg.enable({ emitEvent: false });
      fg.get("capacity")?.disable({ emitEvent: false });
    });
  }

  // ── Abort helpers ──────────────────────────────────────────────────────────
  canAbort(card: any): boolean { return ABORTABLE_COLS.includes(card.col); }

  confirmAbortCard(card: any): void {
    this.cardToAbort = card;
    this.abortReason = "";
    this.abortReasonError = false;
    this.showAbortModal = true;
  }

  cancelAbort(): void {
    this.cardToAbort = null;
    this.abortReason = "";
    this.abortReasonError = false;
    this.showAbortModal = false;
  }

  executeAbort(): void {
    if (!this.cardToAbort) return;
    if (!this.abortReason.trim()) { this.abortReasonError = true; return; }

    const card = this.cardToAbort;
    const payload = {
      ...card,
      col: "completed",
      status: "Aborted",
      abortReason: this.abortReason.trim(),
      abortedAt: new Date().toISOString(),
    };

    this.dataservice.put(`workOrder/program/${card.id}`, payload).subscribe({
      next: () => {
        this.showToast(`Work order "${card.orderNumber}" aborted`, "success");
        this.cards = this._cards.map((c) =>
          c.cardId === card.cardId
            ? { ...c, col: "completed", status: "Aborted", abortReason: this.abortReason.trim() }
            : c
        );
        this.cancelAbort();
      },
      error: () => {
        this.showToast("Failed to abort work order.", "error");
        this.cancelAbort();
      },
    });
  }

  // ── Row cache helpers ──────────────────────────────────────────────────────
  private rowKey(b: number, r: number): string { return `${b}_${r}`; }

  private updateRowCache(b: number, r: number): void {
    this.rowErrorCache.set(this.rowKey(b, r), {
      errors: this.computeTimeErrors(b, r),
      qtyExceeds: this.computeQtyExceeds(b, r),
    });
  }

  getTimeErrors(b: number, r: number): string[] {
    return this.rowErrorCache.get(this.rowKey(b, r))?.errors ?? [];
  }

  isQtyExceedsCapacity(b: number, r: number): boolean {
    return this.rowErrorCache.get(this.rowKey(b, r))?.qtyExceeds ?? false;
  }

  getRowCapacity(b: number, r: number): number {
    return this.getMachineRows(b).at(r).get("capacity")?.value;
  }

  // ── Compute helpers ────────────────────────────────────────────────────────
  private computeQtyExceeds(b: number, r: number): boolean {
    const row = this.getMachineRows(b).at(r);
    const qty = Number(row.get("plannedQty")?.value);
    const capacity = Number(row.get("capacity")?.value);
    if (!qty || !capacity) return false;
    return qty > capacity;
  }

  private computeTimeErrors(b: number, r: number): string[] {
    const row = this.getMachineRows(b).at(r);
    const woStart = this.workOrderForm.get("plannedStartTime")?.value;
    const woEnd = this.workOrderForm.get("plannedEndTime")?.value;
    const procStart = row.get("processstartTime")?.value;
    const procEnd = row.get("processendTime")?.value;
    const errors: string[] = [];
    if (procStart && woStart && new Date(procStart) < new Date(woStart))
      errors.push("Process start cannot be before work order start time.");
    if (procEnd && woEnd && new Date(procEnd) > new Date(woEnd))
      errors.push("Process end cannot exceed work order end time.");
    if (procStart && procEnd && new Date(procEnd) <= new Date(procStart))
      errors.push("Process end must be after process start.");
    if (procStart && procEnd && this.hasMachineTimeOverlap(b, r))
      errors.push("Machine time overlaps with another process.");
    return errors;
  }

  private hasMachineTimeOverlap(batchIndex: number, rowIndex: number): boolean {
    const cur = this.getMachineRows(batchIndex).at(rowIndex);
    const machine = cur.get("machine")?.value;
    const start = cur.get("processstartTime")?.value;
    const end = cur.get("processendTime")?.value;
    if (!machine || !start || !end) return false;
    const cs = new Date(start).getTime();
    const ce = new Date(end).getTime();
    for (let bIdx = 0; bIdx < this.batches.length; bIdx++) {
      const rows = this.getMachineRows(bIdx);
      for (let rIdx = 0; rIdx < rows.length; rIdx++) {
        if (bIdx === batchIndex && rIdx === rowIndex) continue;
        const row = rows.at(rIdx);
        if (row.get("machine")?.value !== machine) continue;
        const s = row.get("processstartTime")?.value;
        const e = row.get("processendTime")?.value;
        if (!s || !e) continue;
        if (cs < new Date(e).getTime() && new Date(s).getTime() < ce) return true;
      }
    }
    return false;
  }

  private revalidateRowsSharingMachine(changedBatch: number, changedRow: number): void {
    const machine = this.getMachineRows(changedBatch).at(changedRow).get("machine")?.value;
    for (let bIdx = 0; bIdx < this.batches.length; bIdx++) {
      const rows = this.getMachineRows(bIdx);
      for (let rIdx = 0; rIdx < rows.length; rIdx++) {
        if (bIdx === changedBatch && rIdx === changedRow) continue;
        if (!machine || rows.at(rIdx).get("machine")?.value !== machine) continue;
        this.rowErrorCache.set(this.rowKey(bIdx, rIdx), {
          errors: this.computeTimeErrors(bIdx, rIdx),
          qtyExceeds: this.computeQtyExceeds(bIdx, rIdx),
        });
      }
    }
  }

  // ── Job selection ──────────────────────────────────────────────────────────
  onJobChange(jobName: string): void {
    if (this.isEditMode && (this.editingCard?.col === "todo" || this.editingCard?.col === "planning")) {
      const orderNumber = this.workOrderForm.get("orderNumber")?.value;

      this.rowErrorCache.clear();
      this.workOrderForm.reset();

      const freshBatches = this.fb.array([this.createBatch()]);

      this.workOrderForm.patchValue(
        { orderNumber, jobName: jobName || "", plannedStartTime: "", plannedEndTime: "" },
        { emitEvent: false }
      );

      this.workOrderForm.setControl("batches", freshBatches);
      this.existingBatchCount = 0;
    } else {
      this.batches.controls.forEach((_, i) => {
        if (this.isExistingBatch(i)) return;
        this.getMachineRows(i).controls.forEach((_, j) => {
          this.getMachineRows(i).at(j).patchValue({
            processName: "", processStatus: "Not Yet Started",
            processstartTime: "", processendTime: "", capacity: "",
          });
          this.rowErrorCache.delete(this.rowKey(i, j));
        });
      });
    }

    const job = this.jobMasterList.find((j) => j.jobName === jobName);
    if (!job) { this.selectedJobProcesses = []; this.selectedJobId = ""; return; }
    this.selectedJobId = job.jobId;
    const seen = new Set<string>();
    this.selectedJobProcesses = job.operationData.filter((op: any) => {
      if (seen.has(op.processName)) return false;
      seen.add(op.processName);
      return true;
    });
  }

  // ── Populate selectedJobProcesses from a jobName (used in editWorkOrder) ──
  private populateJobProcesses(jobName: string): void {
    const job = this.jobMasterList.find((j: any) => j.jobName === jobName);
    if (job) {
      this.selectedJobId = job.jobId;
      const seen = new Set<string>();
      this.selectedJobProcesses = job.operationData.filter((op: any) => {
        if (seen.has(op.processName)) return false;
        seen.add(op.processName);
        return true;
      });
    } else {
      this.selectedJobId = "";
      this.selectedJobProcesses = [];
    }
  }

  onProcessChange(batchIndex: number, rowIndex: number, _processName: string): void {
    const row = this.getMachineRows(batchIndex).at(rowIndex);
    row.patchValue({ processstartTime: "", processendTime: "", capacity: "" });
    this.rowErrorCache.delete(this.rowKey(batchIndex, rowIndex));
  }

  onMachineChange(batchIndex: number, rowIndex: number): void {
    const row = this.getMachineRows(batchIndex).at(rowIndex);
    const oldMachine = row.get("machine")?.value;
    row.patchValue({ processstartTime: "", processendTime: "", capacity: "" });
    this.rowErrorCache.delete(this.rowKey(batchIndex, rowIndex));
    if (oldMachine) {
      for (let bIdx = 0; bIdx < this.batches.length; bIdx++) {
        const rows = this.getMachineRows(bIdx);
        for (let rIdx = 0; rIdx < rows.length; rIdx++) {
          if (bIdx === batchIndex && rIdx === rowIndex) continue;
          if (rows.at(rIdx).get("machine")?.value !== oldMachine) continue;
          this.rowErrorCache.set(this.rowKey(bIdx, rIdx), {
            errors: this.computeTimeErrors(bIdx, rIdx),
            qtyExceeds: this.computeQtyExceeds(bIdx, rIdx),
          });
        }
      }
    }
  }

  onPlannedQtyChange(batchIndex: number, rowIndex: number): void {
    this.updateRowCache(batchIndex, rowIndex);
  }

  onWorkOrderTimeChange(): void {
    const woStart = this.workOrderForm.get("plannedStartTime")?.value;
    const woEnd = this.workOrderForm.get("plannedEndTime")?.value;
    if (!woStart || !woEnd) return;

    if (new Date(woEnd) <= new Date(woStart)) {
      this.showToast("Work order end time must be after start time", "error");
      this.workOrderForm.patchValue({ plannedEndTime: "" });
      return;
    }
    if (new Date(woStart) < new Date(this.getMinWorkOrderStartTime())) {
      this.showToast("Work order start time cannot be in the past", "error");
      this.workOrderForm.patchValue({ plannedStartTime: "", plannedEndTime: "" });
      return;
    }

    this.batches.controls.forEach((_, batchIndex) => {
      if (this.isExistingBatch(batchIndex)) return;
      this.getMachineRows(batchIndex).controls.forEach((_, rowIndex) => {
        const row = this.getMachineRows(batchIndex).at(rowIndex);
        const procStart = row.get("processstartTime")?.value;
        const procEnd = row.get("processendTime")?.value;
        let needsReset = false;
        if (procStart && woStart && new Date(procStart) < new Date(woStart)) needsReset = true;
        if (procEnd && woEnd && new Date(procEnd) > new Date(woEnd)) needsReset = true;
        if (needsReset) row.patchValue({ processstartTime: "", processendTime: "", capacity: "" });
        this.onTimeChange(batchIndex, rowIndex);
      });
    });
  }

  onTimeChange(batchIndex: number, rowIndex: number): void {
    if (this.isExistingBatch(batchIndex)) return;
    const row = this.getMachineRows(batchIndex).at(rowIndex) as FormGroup;
    const procStart = row.get("processstartTime")?.value;
    const procEnd = row.get("processendTime")?.value;
    const errors = this.computeTimeErrors(batchIndex, rowIndex);
    if (errors.length === 0 && procStart && procEnd) {
      this.recalcCapacity(batchIndex, rowIndex);
    } else {
      row.get("capacity")?.setValue("", { emitEvent: false });
    }
    this.updateRowCache(batchIndex, rowIndex);
    this.revalidateRowsSharingMachine(batchIndex, rowIndex);
    this.updateBatchTimes(batchIndex);
  }

  updateBatchTimes(batchIndex: number): void {
    const batch = this.batches.at(batchIndex);
    const rows = this.getMachineRows(batchIndex);
    let minStart: Date | null = null;
    let maxEnd: Date | null = null;
    rows.controls.forEach((row) => {
      const start = row.get("processstartTime")?.value;
      const end = row.get("processendTime")?.value;
      if (start) { const d = new Date(start); if (!minStart || d < minStart) minStart = d; }
      if (end) { const d = new Date(end); if (!maxEnd || d > maxEnd) maxEnd = d; }
    });
    if (minStart && maxEnd) {
      batch.get("batchStartTime")?.setValue(this.formatDateTimeLocal(minStart), { emitEvent: false });
      batch.get("batchEndTime")?.setValue(this.formatDateTimeLocal(maxEnd), { emitEvent: false });
    } else {
      batch.get("batchStartTime")?.setValue("", { emitEvent: false });
      batch.get("batchEndTime")?.setValue("", { emitEvent: false });
    }
  }

  private recalcCapacity(batchIndex: number, rowIndex: number): void {
    const row = this.getMachineRows(batchIndex).at(rowIndex) as FormGroup;
    const processName = row.get("processName")?.value;
    const start = row.get("processstartTime")?.value;
    const end = row.get("processendTime")?.value;
    if (!processName || !start || !end) { row.get("capacity")?.setValue("", { emitEvent: false }); return; }

    const jobName = this.workOrderForm.get("jobName")?.value;
    const job = this.jobMasterList.find((j) => j.jobName === jobName);
    if (!job) return;
    const op = job.operationData.find((o: any) => o.processName === processName);
    if (!op) return;

    const durationSec = (new Date(end).getTime() - new Date(start).getTime()) / 1000;
    if (durationSec <= 0) { row.get("capacity")?.setValue(0, { emitEvent: false }); return; }

    let cycleTimeSec = 0;
    if (op.isCycleTime) {
      cycleTimeSec = (op.runningTime ?? 0) + (op.loadingTime ?? 0) + (op.unloadingTime ?? 0);
    } else {
      cycleTimeSec = (parseFloat(op.targetHours ?? "1") * 3600) / (op.target ?? 1);
    }

    const capacity = cycleTimeSec > 0
      ? Math.floor((durationSec / cycleTimeSec) * (op.partsPerCycle ?? 1))
      : 0;
    row.get("capacity")?.setValue(capacity, { emitEvent: false });
  }

  // ── Batch actions ──────────────────────────────────────────────────────────
  addBatch(): void {
    const batch = this.createBatch();
    batch.get("batchNumber")?.setValue(this.batches.length + 1);
    this.batches.push(batch);
  }

  removeBatch(i: number): void {
    if (this.isExistingBatch(i)) return;
    if (this.batches.length === 1) return;
    this.batches.removeAt(i);
    this.rowErrorCache.forEach((_, key) => {
      if (key.startsWith(`${i}_`)) this.rowErrorCache.delete(key);
    });
    this.promoteLastBatch();
  }

  addMachineRow(batchIndex: number): void {
    if (this.isExistingBatch(batchIndex)) return;
    this.getMachineRows(batchIndex).push(this.createMachineRow());
  }

  removeMachineRow(batchIndex: number, rowIndex: number): void {
    if (this.isExistingBatch(batchIndex)) return;
    if (this.getMachineRows(batchIndex).length === 1) return;
    this.getMachineRows(batchIndex).removeAt(rowIndex);
    this.rowErrorCache.delete(this.rowKey(batchIndex, rowIndex));
    this.promoteLastBatch();
    this.updateBatchTimes(batchIndex);
  }

  // ── Validation ─────────────────────────────────────────────────────────────
  private isFormValid(): boolean {
    let valid = true;
    if (!this.isWorkOrderTimesSet()) valid = false;
    this.batches.controls.forEach((_, i) => {
      if (this.isExistingBatch(i)) return;
      this.getMachineRows(i).controls.forEach((_, j) => {
        this.onTimeChange(i, j);
        if (this.getTimeErrors(i, j).length > 0) valid = false;
        if (this.isQtyExceedsCapacity(i, j)) valid = false;
      });
    });
    if (this.workOrderForm.invalid) valid = false;
    return valid;
  }

  // ── Modals ─────────────────────────────────────────────────────────────────
  addWorkOrder(content: TemplateRef<any>): void {
    this.createForm();
    this.modalService.open(content, { size: "xl", backdrop: "static", centered: true });
  }

  editWorkOrder(card: any, content: TemplateRef<any>): void {
    if (card.col === "completed") return;

    // Guard: if jobMasterList not loaded yet, fetch first then retry
    if (!this.jobMasterList?.length) {
      this.dataservice.get("jobOrder/masterList/" + this.custId).subscribe({
        next: (res: any) => {
          this.jobMasterList = res || [];
          this.editWorkOrder(card, content);
        },
        error: () => { this.showToast("Failed to load job data.", "error"); },
      });
      return;
    }

    this.createForm();
    this.isEditMode = true;
    this.editingCard = card;
    this.existingBatchCount = card.batches?.length ?? 0;

    // ── Populate selectedJobProcesses BEFORE patching form values ──────────
    this.populateJobProcesses(card.jobName);

    // ── Patch top-level fields ─────────────────────────────────────────────
    this.workOrderForm.patchValue({
      orderNumber: card.orderNumber,
      jobName: card.jobName,
      requiredQty: card.requiredQty,
      plannedStartTime: card.plannedStartTime,
      plannedEndTime: card.plannedEndTime,
    });

    // ── Rebuild batches FormArray ──────────────────────────────────────────
    const batchesArray = this.workOrderForm.get("batches") as FormArray;
    while (batchesArray.length) batchesArray.removeAt(0);

    const totalBatches = card.batches?.length ?? 0;

    (card.batches ?? []).forEach((batch: any, bIdx: number) => {
      // const shouldLock = (card.col === "todo" || card.col === "planning")
      //   ? bIdx < totalBatches - 1
      //   : true;

      const shouldLock = card.col === "planning"
        ? false
        : card.col === "todo"
          ? bIdx < totalBatches - 1
          : true;

      const batchGroup = this.fb.group({
        batchNumber: [batch.batchNumber],
        batchStatus: [batch.batchStatus ?? "Not Yet Started"],
        batchStartTime: [{ value: batch.batchStartTime ?? "", disabled: true }],
        batchEndTime: [{ value: batch.batchEndTime ?? "", disabled: true }],
        machineRows: this.fb.array(
          (batch.machineRows ?? []).map((row: any) =>
            this.fb.group({
              machine: [row.machine, Validators.required],
              processId: [row.processId],
              processName: [row.processName, Validators.required],
              processStatus: [row.processStatus ?? "Not Yet Started"],
              processstartTime: [row.processstartTime, Validators.required],
              processendTime: [row.processendTime, Validators.required],
              plannedQty: [row.plannedQty, [Validators.required, Validators.min(1)]],
              capacity: [{ value: row.capacity ?? "", disabled: true }],
            })
          )
        ),
      });

      if (shouldLock) {
        batchGroup.disable();
        // Keep display-only auto-calc fields readable
        batchGroup.get("batchStartTime")?.enable({ emitEvent: false });
        batchGroup.get("batchEndTime")?.enable({ emitEvent: false });
      } else {
        batchGroup.enable({ emitEvent: false });
        batchGroup.get("batchStartTime")?.disable({ emitEvent: false });
        batchGroup.get("batchEndTime")?.disable({ emitEvent: false });
        // Ensure capacity disabled inside enabled batch rows
        const machineRowsArr = batchGroup.get("machineRows") as FormArray;
        machineRowsArr.controls.forEach((rowCtrl: AbstractControl) => {
          (rowCtrl as FormGroup).get("capacity")?.disable({ emitEvent: false });
        });
      }

      batchesArray.push(batchGroup);
    });

    // ── Lock top-level fields for non-todo ─────────────────────────────────
    if (card.col !== "todo" && card.col !== "planning") {
      this.workOrderForm.get("jobName")?.disable({ emitEvent: false });
      this.workOrderForm.get("requiredQty")?.disable({ emitEvent: false });
      this.workOrderForm.get("plannedStartTime")?.disable({ emitEvent: false });
      this.workOrderForm.get("plannedEndTime")?.disable({ emitEvent: false });
    }

    // Always lock orderNumber in edit mode
    this.workOrderForm.get("orderNumber")?.disable({ emitEvent: false });

    this.modalService.open(content, { size: "xl", backdrop: "static", centered: true });
  }

  // ── Save ───────────────────────────────────────────────────────────────────
  saveWorkOrder(modal: any): void {
    this.submitted = true;
    this.workOrderForm.markAllAsTouched();
    if (!this.isFormValid()) {
      this.showToast("Please fix all validation errors before saving.", "error");
      return;
    }

    const val = this.workOrderForm.getRawValue();

    const buildMachineRow = (row: any) => {
      const machine = this.machineData.find((m) => m.deviceName === row.machine);
      const job = this.jobMasterList.find((j) => j.jobId === this.selectedJobId);
      const operation = job?.operationData.find((op: any) => op.processName === row.processName);
      return {
        machine: machine?.deviceName ?? row.machine,
        deviceId: machine?.deviceId ?? null,
        machineId: machine?.machineId ?? null,
        processId: row.processId,
        processName: row.processName,
        processStatus: row.processStatus,
        processstartTime: row.processstartTime,
        processendTime: row.processendTime,
        plannedQty: row.plannedQty,
        capacity: row.capacity,
        partsPerCycle: operation?.partsPerCycle ?? 0,
        countingType: operation?.countingType ?? null,
        runningTime: operation?.runningTime ?? 0,
        loadingTime: operation?.loadingTime ?? 0,
        unloadingTime: operation?.unloadingTime ?? 0,
      };
    };

    const batchesPayload = val.batches.map((batch: any) => ({
      batchNumber: batch.batchNumber,
      batchStatus: batch.batchStatus,
      batchStartTime: batch.batchStartTime,
      batchEndTime: batch.batchEndTime,
      machineRows: batch.machineRows.map(buildMachineRow),
    }));

    if (this.isEditMode && this.editingCard) {
      const payload = {
        ...this.editingCard,
        orderNumber: val.orderNumber,
        jobId: this.selectedJobId,
        jobName: val.jobName,
        requiredQty: val.requiredQty,
        plannedStartTime: val.plannedStartTime,
        plannedEndTime: val.plannedEndTime,
        batches: batchesPayload,
      };
      this.dataservice.put(`workOrder/program/${this.editingCard.id}`, payload).subscribe({
        next: () => {
          this.showToast(`Work order "${val.orderNumber}" updated`, "success");
          modal.dismiss();
          this.loadCards();
        },
        error: () => { this.showToast("Failed to update work order.", "error"); },
      });
    } else {
      const newCard = {
        // cardId: "c" + Date.now(),
        col: "todo",
        status: "Created",
        custId: this.custId,
        orderNumber: val.orderNumber,
        jobId: this.selectedJobId,
        jobName: val.jobName,
        requiredQty: val.requiredQty,
        plannedStartTime: val.plannedStartTime,
        plannedEndTime: val.plannedEndTime,
        batches: batchesPayload,
      };
      this.dataservice.post("workOrder/program", newCard).subscribe({
        next: () => {
          this.showToast(`Work order "${val.orderNumber}" added`, "success");
          modal.dismiss();
          this.loadCards();
        },
        error: () => { this.showToast("Failed to save work order.", "error"); },
      });
    }
  }

  // ── Delete ─────────────────────────────────────────────────────────────────
  confirmDeleteCard(card: any): void { this.cardToDelete = card; this.showDeleteModal = true; }
  cancelDelete(): void { this.cardToDelete = null; this.showDeleteModal = false; }

  executeDelete(): void {
    if (!this.cardToDelete) return;
    const card = this.cardToDelete;
    this.dataservice.delete(`workOrder/program/${card.id}`).subscribe({
      next: () => {
        this.showToast(`Work order "${card.orderNumber}" deleted`, "success");
        // this.cards = this._cards.filter((c) => c.cardId !== card.cardId);
        this.loadCards()
        this.cancelDelete();
      },
      error: () => { this.showToast("Failed to delete work order.", "error"); this.cancelDelete(); },
    });
  }

  // ── Kanban helpers ─────────────────────────────────────────────────────────
  getCards(colId: string): any[] { return this.columnMap.get(colId) ?? []; }
  getColumnLabel(colId: string): string { return this.columns.find((c) => c.id === colId)?.title ?? colId; }
  statusClass(s: string): string { return this.statusCssMap[s] ?? "not-created"; }
  badgeClass(s: string): string { return this.badgeCssMap[s] ?? "badge-not-created"; }
  statusIcon(s: string): string { return this.statusIconMap[s] ?? "ri-question-line"; }

  get pendingCard(): any { return this._cards.find((c) => c.cardId === this.pendingMove?.cardId); }

  trackByColId = (_: number, col: any) => col.id;
  trackByCardId = (_: number, card: any) => card.cardId;
  trackByIndex = (i: number) => i;

  canDragTo(fromCol: string, toCol: string): boolean {
    return (DRAG_RULES[fromCol] ?? []).includes(toCol);
  }

  onDragStart(event: DragEvent, card: any): void {
    this.dragId = card.cardId;
    event.dataTransfer!.effectAllowed = "move";
    event.dataTransfer!.setData("text/plain", card.cardId);
    setTimeout(() => document.getElementById(card.cardId)?.classList.add("dragging"), 0);
  }

  onDragEnd(_event: DragEvent): void {
    if (this.dragId) document.getElementById(this.dragId)?.classList.remove("dragging");
    this.dragOverCol = null;
    this.dragId = null;
  }

  onDragOver(event: DragEvent, colId: string): void {
    const card = this._cards.find((c) => c.cardId === this.dragId);
    if (!card || card.col === colId || !this.canDragTo(card.col, colId)) {
      event.dataTransfer!.dropEffect = "none";
      return;
    }
    event.preventDefault();
    event.dataTransfer!.dropEffect = "move";
    this.dragOverCol = colId;
  }

  onDragLeave(event: DragEvent): void {
    const related = event.relatedTarget as HTMLElement | null;
    const current = event.currentTarget as HTMLElement;
    if (!related || !current.contains(related)) this.dragOverCol = null;
  }

  onDrop(event: DragEvent, toCol: string): void {
    event.preventDefault();
    this.dragOverCol = null;
    const id = event.dataTransfer?.getData("text/plain") ?? this.dragId;
    if (!id) return;
    document.getElementById(id)?.classList.remove("dragging");
    this.dragId = null;
    const card = this._cards.find((c) => c.cardId === id);
    if (!card || card.col === toCol) return;
    if (!this.canDragTo(card.col, toCol)) { this.showToast("This move is not allowed", "error"); return; }
    this.pendingMove = { cardId: id, fromCol: card.col, toCol };
    this.showConfirmModal = true;
  }

  confirmMove(): void {
    if (!this.pendingMove) return;
    const { cardId, fromCol, toCol } = this.pendingMove;
    const card = this._cards.find((c) => c.cardId === cardId);
    if (card) {
      card.col = toCol;
      card.status = STATUS_MAP[toCol] ?? "Created";
      this.update(card)
      // this.cards = [...this._cards];
      this.showToast(`Moved "${card.jobName}" from ${this.getColumnLabel(fromCol)} → ${this.getColumnLabel(toCol)}`, "success");
    }
    this.pendingMove = null;
    this.showConfirmModal = false;
  }

  update(payload: any) {
    this.dataservice.put(`workOrder/program/${payload.id}`, payload).subscribe({
      next: () => {
        this.showToast(`Work order "${payload.orderNumber}" updated`, "success");
        this.loadCards();
      },
      error: () => { this.showToast("Failed to update work order.", "error"); },
    });
  }

  cancelMove(): void { this.pendingMove = null; this.showConfirmModal = false; }

  showToast(msg: string, type: "success" | "error"): void {
    clearTimeout(this.toastTimer);
    this.toastMsg = msg;
    this.toastType = type;
    this.toastVisible = true;
    this.toastTimer = setTimeout(() => (this.toastVisible = false), 3000);
  }
}